import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { useMemo, useState } from 'react';

import { formatDecimalSigned, formatSig, type AnswerCheck } from './energyCalorimetry';
import {
  FUELS,
  HESS_MULTIPLIERS,
  HESS_SETS,
  checkFuelPrediction,
  combineHess,
  describeMismatch,
  describeMultiplier,
  energyPerGram,
  formatSpeciesVector,
  fuelValue,
  type FuelBasis,
  type HessSet,
} from './energyCalorimetrySenior';
import { FeedbackCard, ValueBars } from './EnergyCalorimetryVisuals';

const DEFAULT_MULTIPLIER = 1;

function hessCheck(set: HessSet, multipliers: number[]): AnswerCheck {
  const combination = combineHess(set, multipliers);
  if (!combination) {
    return { verdict: 'invalid', feedback: 'Choose a supported multiplier for every equation.' };
  }
  if (combination.matchesTarget) {
    return {
      verdict: 'correct',
      feedback: `Target reached. ΔH = ${formatDecimalSigned(combination.deltaHkJ, 1)} kJ mol⁻¹ (accepted value ${formatDecimalSigned(set.referenceDeltaHkJ, 1)} kJ mol⁻¹). Reversing an equation changes the sign of ΔH; multiplying it multiplies ΔH.`,
    };
  }
  return {
    verdict: 'other',
    feedback: combination.mismatches.map(describeMismatch).join(' '),
  };
}

export function HessPanel() {
  const [set, setSet] = useState<HessSet>(HESS_SETS[0]);
  const [multipliers, setMultipliers] = useState<number[]>(() =>
    HESS_SETS[0].steps.map(() => DEFAULT_MULTIPLIER),
  );
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('HessPanel', { set: tutorSelection(set), multipliers, checked });
  const combination = useMemo(() => combineHess(set, multipliers), [set, multipliers]);

  const choose = (entry: HessSet) => {
    setSet(entry);
    setMultipliers(entry.steps.map(() => DEFAULT_MULTIPLIER));
    setChecked(false);
  };

  return (
    <section className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
      <div className="flex flex-wrap gap-2" aria-label="Choose a Hess's law target">
        {HESS_SETS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={set.id === entry.id}
            onClick={() => choose(entry)}
            className={`rounded-full border px-4 py-2 text-sm font-extrabold ${set.id === entry.id ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
          >
            {entry.name}
          </button>
        ))}
      </div>
      <div className="mt-4 rounded-2xl border border-brand-sunshine/40 bg-wash-sunshine p-4">
        <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">Target</p>
        <p className="mt-1 break-words font-mono text-base font-extrabold text-ink">{set.target}</p>
      </div>
      <ol className="mt-4 space-y-3">
        {set.steps.map((step, index) => (
          <li
            key={step.equation}
            className="grid gap-3 rounded-2xl border border-hairline bg-surface p-4 sm:grid-cols-[minmax(0,1fr)_12rem] sm:items-center"
          >
            <div className="min-w-0">
              <p className="break-words font-mono text-sm font-bold text-ink">
                ({index + 1}) {step.equation}
              </p>
              <p className="mt-1 text-xs font-bold text-slate2">
                ΔH = {formatDecimalSigned(step.deltaHkJ, 1)} kJ
              </p>
            </div>
            <label className="text-xs font-extrabold text-ink">
              Use equation {index + 1}
              <select
                aria-label={`Multiplier for equation ${index + 1}`}
                value={multipliers[index]}
                onChange={(event) => {
                  const next = [...multipliers];
                  next[index] = Number(event.target.value);
                  setMultipliers(next);
                  setChecked(false);
                }}
                className="mt-1 w-full rounded-xl border border-hairline bg-white px-3 py-2 text-sm font-bold text-ink"
              >
                {HESS_MULTIPLIERS.map((value) => (
                  <option key={value} value={value}>
                    {describeMultiplier(value)}
                  </option>
                ))}
              </select>
            </label>
          </li>
        ))}
      </ol>
      {combination ? (
        <div className="mt-4 rounded-2xl bg-surface p-4" aria-live="polite">
          <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">
            Your sum (species cancelled)
          </p>
          <p className="mt-1 break-words font-mono text-sm font-bold text-ink">
            {formatSpeciesVector(combination.net)}
          </p>
          <p className="mt-1 text-sm font-extrabold text-ink">
            ΣΔH = {formatDecimalSigned(combination.deltaHkJ, 1)} kJ
          </p>
        </div>
      ) : null}
      <button type="button" onClick={() => setChecked(true)} className="btn-pill-primary mt-5">
        Check combination
      </button>
      {checked ? (
        <div className="mt-4">
          <FeedbackCard title="Hess's law" check={hessCheck(set, multipliers)} />
        </div>
      ) : null}
      <p className="mt-4 text-xs font-medium leading-5 text-slate2">
        Values are standard enthalpies (25 °C, 100 kPa, water as a liquid) commonly quoted from the
        NIST Chemistry WebBook; small differences from your data sheet come from rounding.
      </p>
    </section>
  );
}

const BASIS_LABELS: Record<FuelBasis, { label: string; unit: string }> = {
  'per-mol': { label: 'Per mole', unit: 'kJ mol⁻¹' },
  'per-gram': { label: 'Per gram', unit: 'kJ g⁻¹' },
};

export function FuelsPanel() {
  const [prediction, setPrediction] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [basis, setBasis] = useState<FuelBasis>('per-gram');
  useScienceTutorSection('FuelsPanel', { prediction, revealed, basis });
  const rows = FUELS.map((entry) => ({
    id: entry.id,
    label: entry.name,
    value: fuelValue(entry, basis),
  }));

  return (
    <section className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
      <fieldset>
        <legend className="text-base font-extrabold text-ink">
          Predict: which fuel releases the most energy per gram when it burns completely?
        </legend>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {FUELS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={prediction === entry.id}
              onClick={() => {
                setPrediction(entry.id);
                setRevealed(false);
              }}
              className={`rounded-2xl border px-3 py-2 text-left text-sm font-extrabold ${prediction === entry.id ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
            >
              {entry.name}
              <span className="block text-xs font-bold opacity-80">{entry.formula}</span>
            </button>
          ))}
        </div>
      </fieldset>
      <button
        type="button"
        disabled={!prediction}
        onClick={() => setRevealed(true)}
        className="btn-pill-primary mt-5 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Reveal the combustion data
      </button>
      {revealed && prediction ? (
        <div className="mt-5 space-y-4">
          <FeedbackCard title="Fuel" check={checkFuelPrediction(prediction)} />
          <div className="flex rounded-full bg-surface p-1" aria-label="Compare fuels">
            {(Object.keys(BASIS_LABELS) as FuelBasis[]).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={basis === option}
                onClick={() => setBasis(option)}
                className={`flex-1 rounded-full px-3 py-2 text-sm font-extrabold ${basis === option ? 'bg-ink text-white' : 'text-slate2'}`}
              >
                {BASIS_LABELS[option].label}
              </button>
            ))}
          </div>
          <div aria-label={`Fuel energy ${BASIS_LABELS[basis].label.toLowerCase()}`}>
            <ValueBars rows={rows} unit={BASIS_LABELS[basis].unit} highlightId={prediction} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[30rem] text-left text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-slate2">
                  <th className="py-2 pr-3">Fuel</th>
                  <th className="py-2 pr-3">M (g mol⁻¹)</th>
                  <th className="py-2 pr-3">ΔcH (kJ mol⁻¹)</th>
                  <th className="py-2">Energy (kJ g⁻¹)</th>
                </tr>
              </thead>
              <tbody>
                {FUELS.map((entry) => (
                  <tr key={entry.id} className="border-t border-hairline font-bold text-ink">
                    <td className="py-2 pr-3">
                      {entry.name} ({entry.formula}, {entry.state})
                    </td>
                    <td className="py-2 pr-3">{entry.molarMass}</td>
                    <td className="py-2 pr-3">{String(entry.combustionkJPerMol).replace('-', '−')}</td>
                    <td className="py-2">{formatSig(energyPerGram(entry), 3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs font-medium leading-5 text-slate2">
            Standard enthalpies of combustion (NIST Chemistry WebBook, 25 °C, 100 kPa, complete
            combustion to CO₂ and liquid water). Energy per gram ignores storage: hydrogen needs
            heavy pressurised tanks, so per-litre and per-tank comparisons differ.
          </p>
        </div>
      ) : null}
    </section>
  );
}
